import React, { useState } from 'react';

import { useMutation } from '@betterment/js-runtime';
import {
  Banner,
  Box,
  Button,
  FormikField as Field,
  H1,
  IconButton,
  PancakeStack,
  RadioGroup,
  WorkflowHeader,
} from '@betterment/samba-react';
import { Form, Formik } from 'formik';

import {
  AdvisorGoalCreationFlowSpouseAccountParams,
  AdvisorGoalCreationFlowSpouseAccountResponse,
} from '../../models/participantSelectionStep';
import { navigate } from '../../utils/path-helpers';

export interface ParticipantSelectionStepProps {
  spouseName: string;
  householdId: string;
  ownerName: string;
  goalId: string;
}

export const ParticipantSelectionStep: React.FC<ParticipantSelectionStepProps> = ({
  spouseName,
  householdId,
  ownerName,
  goalId,
}) => {
  const [isDismissedBanner, setIsDismissedBanner] = useState(false);
  const [createGoalCreationFlowCreation] = useMutation<
    AdvisorGoalCreationFlowSpouseAccountResponse,
    AdvisorGoalCreationFlowSpouseAccountParams
  >('/api/v1/advisor_goal_creation_flows/solo_401k_spouse_account_creation');

  const closeHandler = () => {
    navigate(`/households/${householdId}`);
  };

  const redirectSubmission = async (values: { spouseParticipant: string }) => {
    if (values.spouseParticipant === 'yes') {
      const params = await createGoalCreationFlowCreation({ goalId });
      // @ts-expect-error TS18048 violates strict null checks
      return navigate(params.data.redirectUrl);
    }

    return navigate(`/households/${householdId}/other_spouse_participant/${goalId}`);
  };

  return (
    <PancakeStack css={{ maxWidth: 'unset' }} title="Add Spouse participant">
      <PancakeStack.Header css={{ maxWidth: 'unset' }}>
        <WorkflowHeader css={{ borderBottom: 'solid 1px', width: '100%', maxWidth: 'unset', borderColor: 'border' }}>
          <WorkflowHeader.Leading>
            <IconButton label="back to household" name="LeftArrowSmall" spacing="default" onClick={closeHandler} />
          </WorkflowHeader.Leading>
          <WorkflowHeader.Title fontVariant="text50">Add spouse participant</WorkflowHeader.Title>
          <WorkflowHeader.Trailing>
            <IconButton label="back to household" name="X" spacing="default" onClick={closeHandler} />
          </WorkflowHeader.Trailing>
        </WorkflowHeader>
      </PancakeStack.Header>
      <PancakeStack.Main>
        <Box css={{ width: '100%', maxWidth: '584px', marginTop: 'space.600', justifySelf: 'center' }}>
          <Formik initialValues={{ spouseParticipant: 'yes' }} onSubmit={redirectSubmission}>
            <Form>
              <Box css={{ display: 'grid', gap: 'space.400' }}>
                <H1>
                  Add {spouseName} as the spouse participant for {ownerName}’s solo 401(k) plan?
                </H1>
                <Field name="spouseParticipant">
                  <Field.Control>
                    <RadioGroup>
                      <RadioGroup.ItemContainer>
                        <RadioGroup.Item value="yes" aria-label="yes-option" />
                        <RadioGroup.ItemLabel>Yes</RadioGroup.ItemLabel>
                      </RadioGroup.ItemContainer>
                      <RadioGroup.ItemContainer>
                        <RadioGroup.Item value="no" aria-label="no-option" />
                        <RadioGroup.ItemLabel>No</RadioGroup.ItemLabel>
                      </RadioGroup.ItemContainer>
                    </RadioGroup>
                  </Field.Control>
                </Field>
                <Box>
                  <Button type="submit">Continue</Button>
                </Box>

                {!isDismissedBanner && (
                  <Banner variant="info">
                    <Banner.Content>
                      Your client’s solo 401(k) will include Traditional and Roth accounts, and can be managed
                      independently or combined after account setup.
                    </Banner.Content>
                    <Banner.Close aria-label="close-banner" onClick={() => setIsDismissedBanner(true)} />
                  </Banner>
                )}
              </Box>
            </Form>
          </Formik>
        </Box>
      </PancakeStack.Main>
    </PancakeStack>
  );
};
